import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { languageTransfer } from "../../../utils/languageTransfer";
import i18next from "i18next";
import GameItem from "../../GameItem/GameItem";
import LoadingBlock from "../../Loading/LoadingBlock";
import NotFound from "../../ProgressBlock/NotFound";
import { Box, chakra, Image, shouldForwardProp } from "@chakra-ui/react";
import { isValidMotionProp, motion } from "framer-motion";
import { gameListFromBottom } from "../../../animateConstant";
import { COMMON_WEB_PATH } from "../../../constant";
import useIsLandScapeMachine from "../../../hook/useIsLandScapeMachine";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const GameSlideSelector = () => {
  const [searchParams] = useSearchParams();

  const searchParamsType = searchParams.get("type") || "Popular";

  const [isLoading, setIsLoading] = useState(true);

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const { newGameList, exclusiveBrandGames, highlightGames } =
    isHandleAllGame || {};

  const gameList = useMemo(() => {
    // 特別處理customSubType="New" 的情況
    if (searchParamsType === "New") {
      return (newGameList || []).slice(0, 8); // 直接返回 newGameList 只返回前8個 不需要其他過濾
    }

    // 特別處理customSubType="exclusiveBrandGames"的情況 顯示全部獨家遊戲
    if (searchParamsType === "exclusiveBrandGames") {
      return exclusiveBrandGames || []; // 直接返回全部獨家遊戲
    }

    // 特別處理customSubType="Popular"的情況 從highlightGames取前40個
    else {
      const gamesArray = Object.values(highlightGames || {});

      // 根據 game_tags 排序
      const sortedGames = gamesArray.sort((a, b) => {
        const aHotTag = a.game_tags?.find((tag) => tag.startsWith("hot_"));
        const bHotTag = b.game_tags?.find((tag) => tag.startsWith("hot_"));

        // 如果沒有 hot tag，排到最後面
        if (!aHotTag) return 1;
        if (!bHotTag) return -1;

        const aOrder = parseInt(aHotTag.split("_")[1]);
        const bOrder = parseInt(bHotTag.split("_")[1]);

        return aOrder - bOrder;
      });

      // 過濾掉沒有 hot tag 的遊戲並返回前40個
      return sortedGames.slice(0, 40);
    }
  }, [searchParamsType, newGameList, exclusiveBrandGames, highlightGames]);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isLoading]);

  useEffect(() => {
    setIsLoading(true);
  }, [searchParamsType]);

  const isLandScapeMachine = useIsLandScapeMachine();

  return (
    <>
      <LoadingBlock size="50px" commonLoading={isLoading}>
        {gameList?.length === 0 && <NotFound />}
        <ChakraBox
          key={searchParamsType}
          {...gameListFromBottom}
          position="relative"
          h="100%"
        >
          <Splide
            hasTrack={false}
            className="h-full w-full"
            options={{
              type: "slide",
              rewind: true,
              drag: "free",
              gap: 10,
              arrows: true,
              fixedWidth: isLandScapeMachine ? `33%` : `22.5%`,
              pagination: false,
            }}
          >
            <Box className="splide__arrows">
              <button
                className="splide__arrow splide__arrow--prev"
                style={{
                  background: "none",
                  width: "80px",
                  height: "80px",
                  opacity: 1,
                }}
              >
                <Image
                  w="100%"
                  h="100%"
                  objectFit={"contain"}
                  src={`${COMMON_WEB_PATH}/now888/platform_list/left_mobile.png`}
                />
              </button>
              <button
                className="splide__arrow splide__arrow--next"
                style={{
                  background: "none",
                  width: "80px",
                  height: "80px",
                  opacity: 1,
                }}
              >
                <Image
                  w="100%"
                  h="100%"
                  objectFit={"contain"}
                  src={`${COMMON_WEB_PATH}/now888/platform_list/right_mobile.png`}
                />
              </button>
            </Box>
            <SplideTrack>
              {gameList?.map((item, index) => {
                const gameName =
                  item?.[
                    `gameName${languageTransfer({
                      lang: i18next.language,
                    })}`
                  ];
                const gameNameEn = item?.gameNameEn;
                const platform = item?.game_platform;
                const gameSubType = item?.game_subtype;

                const gameCode = item?.gameCode;
                const gameType = item?.gameType;
                return (
                  <SplideSlide>
                    <GameItem
                      gameProps={{
                        gameName,
                        platform,
                        gameCode,
                        gameType,
                        gameNameEn,
                        gameSubType,
                      }}
                      originProps={item}
                      itemIndex={index}
                      longLayout
                      machineLayout
                    />
                  </SplideSlide>
                );
              })}
            </SplideTrack>
          </Splide>
        </ChakraBox>
      </LoadingBlock>
    </>
  );
};

export default GameSlideSelector;
